import React from 'react';

import { initStore, useInitializeApplication } from '@apps/core';

import { AgentChatProvider } from '@lumapps/agent-chat/components/AgentChatProvider';
import { AiAgentsChatWithButton } from '@lumapps/agent-chat/components/AiAgentChatWithButton';
import { usePublicUniqueId } from '@lumapps/analytics-tracking/hooks/usePublicUniqueId';
import { GracePeriodBanner } from '@lumapps/customer/components/GracePeriodBanner';
import { CustomizationComponent, Targets, PLACEMENT } from '@lumapps/customizations/api';
import { DigitalAssistant } from '@lumapps/digital-assistant/components/DigitalAssistant';
import { Footer } from '@lumapps/footer';
import { GdprBanner } from '@lumapps/gdpr-banner';
import { Header } from '@lumapps/header';
import { Metrics } from '@lumapps/metric';
import { GoogleAnalyticsAndTagManager } from '@lumapps/metric/components/GoogleAnalyticsAndTagManager';
import { MobileAppModal } from '@lumapps/mobile-app-modal';
import { NannyDetails } from '@lumapps/nanny-details/components/DetailModal';
import { getBaseUrl } from '@lumapps/router';
import { AppShell } from '@lumapps/shell/components/AppShell';
import { TermsAndConditions } from '@lumapps/terms-and-conditions/components/Modal';
import { IsConnectedAsBanner } from '@lumapps/user/components/IsConnectedAsBanner';

import { rootReducer, transforms } from '../../ducks';
import Loading from '../Loading';
import Pages from '../Pages';
import { NO_SLIDESHOW_PAGES } from '../Pages/configuration';

declare const window: any;

/**
 * Main entrypoint of the front office application. It takes care of setting up:
 * - initial state of the application and setting up redux
 */
const App = () => {
    const { initialState, translationsState } = useInitializeApplication();

    /** Set a LUMAPPS-PUBLIC-UNIQUE-ID in the localStorage */
    usePublicUniqueId(initialState);

    /**
     * Since the initial state and the translations are blocking for rendering, while they are not here,
     * we want to show a Loading component in order to provide some feedback to the user
     */
    if (!initialState && !translationsState) {
        return <Loading />;
    }

    window.lumappsPerformances.loaderHidden = Date.now();

    /**
     * We retrieve the base URL that includes the customer and the instance slug
     * and use it as the base path for the react-router implementation
     */
    const baseUrl = getBaseUrl(window.location.pathname);
    const store = initStore(rootReducer, initialState, transforms);

    return (
        <AppShell
            errorBoundaryProps={{
                fallback: <Loading />,
            }}
            baseUrl={baseUrl}
            store={store}
            translationsState={translationsState}
            shouldDisplayCustomizations
        >
            <AgentChatProvider>
                <Metrics />
                {/*
                 * The GDPR banner, notification & GracePeriodBanner must be the first element of the DOM for a11y purposes
                 * as users might want to interact with them before navigating the application.
                 */}
                <GdprBanner />
                <IsConnectedAsBanner />
                <GracePeriodBanner />
                <GoogleAnalyticsAndTagManager />
                <CustomizationComponent target={Targets.APP} placement={PLACEMENT.LEFT} />
                <Header hideContentHeaderRoutes={NO_SLIDESHOW_PAGES} />
                <Pages basePath={baseUrl} />
                <Footer />
                <CustomizationComponent target={Targets.APP} placement={PLACEMENT.UNDER} />
                <CustomizationComponent target={Targets.APP} placement={PLACEMENT.RIGHT} />
                <DigitalAssistant />
                <AiAgentsChatWithButton showButton />
                <TermsAndConditions />
                <MobileAppModal />
                <NannyDetails />
            </AgentChatProvider>
        </AppShell>
    );
};

export { App };
